import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import { getUserProfile, addUserRole, switchUserRole, syncUserRoles, selectRole } from '../services/userService';
import { authCache } from '../utils/authCache';
import websocketService from '../services/websocketService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);

  // 🚀 OPTIMIZATION: Non-blocking profile data loading
  const loadProfileDataInBackground = useCallback(async () => {
    try {
      // Check if we have cached profile data
      const cachedProfile = authCache.getCachedUserProfile();
      if (cachedProfile) {
        console.log('Using cached profile data');
        return;
      }
      
      // Load profile data in background
      const profileData = await getUserProfile();
      
      // Cache the profile data
      authCache.cacheUserProfile(profileData);
      
      // Update roles if profile has different data
      if (profileData.userTypes || profileData.primaryRole) {
        const rolesFromApi = profileData.userTypes || [];
        const activeRoleFromApi = profileData.primaryRole;
        
        // Only update if there are meaningful differences
        setUserRoles(prevRoles => {
          const mergedRoles = [...new Set([...prevRoles, ...rolesFromApi])];
          return mergedRoles.length !== prevRoles.length ? mergedRoles : prevRoles;
        });
        
        if (activeRoleFromApi) {
          setActiveRole(prevRole => activeRoleFromApi !== prevRole ? activeRoleFromApi : prevRole);
        }
      }
      
    } catch (profileErr) {
      // Silent error handling for background profile loading
      console.warn('Background profile loading failed:', profileErr);
    }
  }, []);

  // Optimized check authentication state with caching
  const checkAuthState = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      // 🚀 OPTIMIZATION: Try cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedAttributes = authCache.getCachedUserAttributes();
        // Only use cache when custom:primaryRole is already set — if it's absent the
        // auto-init path below must run to set the role and refresh the JWT.
        if (cachedAttributes && cachedAttributes['custom:primaryRole']) {
          console.log('Using cached user attributes');
          const { roles, activeRole } = authCache.extractRoleInfo(cachedAttributes);
          
          // Set state with cached data immediately for fast UI updates
          setUserRoles(roles);
          setActiveRole(activeRole);
          
          // Still get current user for the user object, but skip attribute parsing
          try {
            const currentUser = await Auth.currentAuthenticatedUser();
            setUser(currentUser);
            setLoading(false);
            setError(null);
            
            // Load profile data in background (non-blocking)
            loadProfileDataInBackground();
            return;
          } catch (authErr) {
            // If auth fails, clear cache and fall through to full auth check
            authCache.clearAll();
          }
        }
      }
      
      // 🚀 OPTIMIZATION: Get user first for immediate redirect capability
      const currentUser = await Auth.currentAuthenticatedUser();
      const attributes = currentUser.attributes;
      
      // Cache attributes immediately
      authCache.cacheUserAttributes(attributes);
      
      // Extract role information efficiently
      const { roles, activeRole, originalUserType } = authCache.extractRoleInfo(attributes);

      // Auto-initialize role for new users: registration sets custom:user_type but not
      // custom:primaryRole — the backend's roleGuard reads primaryRole from the JWT and
      // returns 403 when it's absent. Call selectRole once to set it in Cognito + DynamoDB.
      if (!attributes['custom:primaryRole'] && originalUserType) {
        try {
          // Set role in Cognito. If it's already set (400) that's fine — still refresh.
          try {
            await selectRole(originalUserType);
          } catch (selectErr) {
            const status = selectErr?.statusCode || selectErr?.response?.status;
            if (status !== 400) throw selectErr; // re-throw unexpected errors
            // 400 = role already set in Cognito — proceed to token refresh anyway
          }
          // Must use refreshSession (not currentAuthenticatedUser({ bypassCache: true })).
          // bypassCache only fetches fresh user attributes — it does NOT refresh the JWT
          // (ID token). The backend reads custom:primaryRole from the JWT, so we need a
          // new token. refreshSession exchanges the refresh token with Cognito and stores
          // the new ID/access tokens in localStorage so all subsequent API calls use them.
          const cognitoUser = await Auth.currentAuthenticatedUser();
          await new Promise((resolve) => {
            const session = cognitoUser.getSignInUserSession();
            if (!session) return resolve(null);
            cognitoUser.refreshSession(session.getRefreshToken(), (err, newSession) => {
              if (!err && newSession) cognitoUser.setSignInUserSession(newSession);
              resolve(null);
            });
          });
          const refreshedUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
          const freshAttrs = refreshedUser.attributes;
          authCache.clearAll();
          authCache.cacheUserAttributes(freshAttrs);
          const { roles: freshRoles, activeRole: freshActiveRole } = authCache.extractRoleInfo(freshAttrs);
          setUser(refreshedUser);
          setUserRoles(freshRoles);
          setActiveRole(freshActiveRole);
          setLoading(false);
          setError(null);
          loadProfileDataInBackground();
          websocketService.connect().catch(err => console.error('Failed to connect to WebSocket:', err));
          return;
        } catch (initErr) {
          console.warn('Auto role initialization failed, continuing with derived role:', initErr);
        }
      }

      // Update state immediately with Cognito data
      setUser(currentUser);
      setUserRoles(roles);
      setActiveRole(activeRole);
      setLoading(false);
      setError(null);

      // 🚀 OPTIMIZATION: Load profile data in background (non-blocking)
      loadProfileDataInBackground();

      // Initialize WebSocket connection for real-time communication
      websocketService.connect().catch(err => {
        console.error('Failed to connect to WebSocket:', err);
      });
      
    } catch (err) {
      // Clear cache on auth failure
      authCache.clearAll();
      
      // Handle authentication errors
      setUser(null);
      setUserRoles([]);
      setActiveRole(null);
      setLoading(false);
      
      // Amplify v5 throws different values for "not logged in" depending on
      // code path and token state — none of these are real errors on public pages.
      const isExpectedUnauthenticated =
        err === 'No current user' ||
        err === 'The user is not authenticated' ||
        err?.message === 'The user is not authenticated' ||
        err?.code === 'UserNotFoundException';

      if (!isExpectedUnauthenticated) {
        setError('Authentication error');
        console.error('Authentication error:', err);
      }
    }
  }, [loadProfileDataInBackground]);

  // Initial auth check
  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  // Sign in
  const signIn = async (username, password) => {
    try {
      setLoading(true);
      const result = await Auth.signIn(username, password);
      
      // Handle NEW_PASSWORD_REQUIRED challenge
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setLoading(false);
        return result; // Return the challenge result directly
      }
      
      // For successful sign-in, refresh auth state
      await checkAuthState();
      return result;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Complete new password setup
  const completeNewPassword = async (user, newPassword) => {
    try {
      setLoading(true);
      const result = await Auth.completeNewPassword(user, newPassword);
      await checkAuthState();
      return result;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setLoading(true);

      // Disconnect WebSocket before signing out
      websocketService.disconnect();

      // Use global sign out to handle both federated and regular sign-outs
      await Auth.signOut({ global: true });

      // Clear all cached data
      authCache.clearAll();

      setUser(null);
      setUserRoles([]);
      setActiveRole(null);
      setError(null);
    } catch (err) {
      setError('Error signing out');
      console.error('Error signing out:', err);
    } finally {
      setLoading(false);
    }
  };

  // Add role
  const addRole = async (roleType, profileData) => {
    try {
      setLoading(true);
      const response = await addUserRole(roleType, profileData);

      // Clear stale cache so checkAuthState fetches fresh Cognito attributes
      authCache.clearAll();

      // Refresh the JWT session so the new custom:userTypes lands in the ID token.
      // bypassCache only refreshes attributes — it does NOT issue a new JWT.
      // refreshSession exchanges the refresh token with Cognito and stores new tokens.
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        await new Promise((resolve) => {
          const session = cognitoUser.getSignInUserSession();
          if (!session) return resolve(null);
          cognitoUser.refreshSession(session.getRefreshToken(), (err, newSession) => {
            if (!err && newSession) cognitoUser.setSignInUserSession(newSession);
            resolve(null);
          });
        });
      } catch (refreshErr) {
        console.warn('JWT refresh after addRole failed:', refreshErr);
      }

      // Force a full auth state refresh with the new token
      await checkAuthState(true);

      return response;
    } catch (err) {
      setError('Error adding role');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Switch role
  const switchRole = async (role) => {
    try {
      setLoading(true);
      const response = await switchUserRole(role);

      if (response.success) {
        // Optimistically update activeRole immediately so ProtectedRoute
        // allows navigation before the background refresh completes.
        // Without this, checkAuthState() returns stale cached data and
        // ProtectedRoute redirects back to /switch-to/:role in a loop.
        setActiveRole(role);
        authCache.clearAll();
        // Background refresh — do NOT await (would re-enter cache read path)
        checkAuthState(true);
      }

      return response.success;
    } catch (err) {
      setError('Error switching role');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Sync roles
  const syncRoles = async () => {
    try {
      setLoading(true);
      const response = await syncUserRoles();
      
      // Force refresh of auth state to get updated roles
      await checkAuthState();
      
      return response;
    } catch (err) {
      setError('Error syncing roles');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    userRoles,
    activeRole,
    userType: activeRole, // Computed property for backward compatibility
    signIn,
    signOut,
    completeNewPassword,
    addRole,
    switchRole,
    syncRoles,
    checkAuthState
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
